import { useState, useRef } from "react";
import { useListsFilter } from "@/contexts/ListsFilterContext";
import { ListsGrid } from "@/components/lists/ListsGrid";
import { ListsList } from "@/components/lists/ListsList";
import { CandidatesViewOptions, SortField } from "@/components/candidates/CandidatesViewOptions";
import { CandidatesPagination } from "@/components/candidates/CandidatesPagination";
import { useUserFavorites } from "@/hooks/favorites/useUserFavorites";
import { List } from "@/types/list";
import { useTranslation } from "react-i18next";
import { matchesListSearch } from "@/utils/searchUtils";

interface ListsContentProps {
  lists: List[];
  isLoading: boolean;
}

export const ListsContent = ({ lists, isLoading }: ListsContentProps) => {
  const {
    searchTerm,
    showFavoritesOnly,
    sortField,
    setSortField,
    sortOrder,
    setSortOrder,
    viewMode,
    setViewMode,
    currentPage,
    setCurrentPage
  } = useListsFilter();

  const { favorites } = useUserFavorites('list');

  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);

  const ITEMS_PER_PAGE = 12;

  // Filter and sort lists using universal search
  const filteredAndSortedLists = lists
    .filter(list => {
      const matchesSearch = matchesListSearch(list, searchTerm);
      const matchesFavorites = !showFavoritesOnly || favorites.has(list.id);
      return matchesSearch && matchesFavorites;
    })
    .sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case "name":
          comparison = a.name.localeCompare(b.name);
          break;
        case "dateCreated":
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
        case "candidateCount":
          comparison = (a.candidate_count || 0) - (b.candidate_count || 0);
          break;
        default:
          comparison = 0;
      }
      return sortOrder === "asc" ? comparison : -comparison;
    });

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedLists.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedLists = filteredAndSortedLists.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  // Reset to first page when sort field or order changes
  const handleSortFieldChange = (field: SortField) => {
    setSortField(field);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Direct scroll without any transition
    if (containerRef.current) {
      const scrollableParent = containerRef.current.closest('.overflow-y-auto') as HTMLElement;
      if (scrollableParent) {
        // Force immediate scroll without animation
        scrollableParent.style.scrollBehavior = 'auto';
        scrollableParent.scrollTop = 0;
        scrollableParent.style.scrollBehavior = '';
      }
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="h-10 w-64 bg-gray-200 rounded animate-pulse"></div>
          <div className="h-10 w-48 bg-gray-200 rounded animate-pulse"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-48 bg-gray-200 rounded-sm animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  if (lists.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="mx-auto w-24 h-24 bg-gray-100 rounded-sm flex items-center justify-center mb-4">
          <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {t("lists.empty.title", "No lists yet")}
        </h3>
        <p className="text-gray-600 mb-4">
          {t("lists.empty.description", "Create your first list to organize candidates")}
        </p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="flex flex-col h-full">
      <CandidatesViewOptions 
        sortField={sortField}
        setSortField={handleSortFieldChange}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        viewMode={viewMode}
        setViewMode={setViewMode}
      />
      
      <div className="flex-1 overflow-y-auto min-h-0">
        {viewMode === "card" ? (
          <ListsGrid 
            lists={lists}
            filteredLists={paginatedLists}
            isLoading={isLoading}
            favorites={favorites}
          />
        ) : (
          <ListsList 
            lists={lists}
            filteredLists={paginatedLists}
            isLoading={isLoading}
            favorites={favorites}
          />
        )}
      </div>
      
      {totalPages > 1 && (
        <CandidatesPagination 
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
};
